import { useState } from "react";
import LazyLoad from "react-lazyload";

import dynamic from "next/dynamic";

import ButtonNew from "components/atoms/ButtonNew/index";
import { FaCheckCircle } from "components/atoms/Icon/fa/FaCheckCircle";
import { HiOutlineChevronRight } from "components/atoms/Icon/hi/HiOutlineChevronRight";
import Img from "components/atoms/Img";
import LinkRouter from "components/atoms/LinkRouter";
import Title from "components/atoms/Title";
import PageType from "components/molecules/PageType";
import Banner from "components/organisms/Banner";
import Faqs from "components/organisms/Faqs";

import EconomicPricing from "./EconomicPricing";
const NewsSlider = dynamic(() => import("components/organisms/NewsSlider"), {
  ssr: false,
});

const TableRow = ({
  childrenLeft,
  childrenMiddle,
  childrenRight,
  isHeader,
  index,
  hidden,
  onClick,
}) => {
  return (
    <div
      className={`row ${isHeader && "border-b "} ${
        (index + 1) % 2 == 1 ? "bg-[#F7FDFF]" : "bg-white"
      }`}
    >
      <div
        className={
          "col-4 p-3 font-medium text-bbc-brand-600 lg:col-2 lg:block lg:p-6 lg:pl-10"
        }
      >
        {childrenLeft}
      </div>
      <div
        className={`col-8 p-4 text-lg lg:col-5 lg:p-6 ${
          isHeader
            ? "text-center font-medium text-bbc-brand-600 lg:font-semibold"
            : "text-left"
        }
          ${hidden ? "hidden" : "block"}  
         lg:block `}
      >
        {isHeader ? (
          <>
            <span className="align-middle">{childrenMiddle}</span>
            <HiOutlineChevronRight
              size={32}
              onClick={onClick}
              className={
                "pointer-events float-right inline-block cursor-pointer rounded-full bg-bbc-brand-100 p-2 align-middle shadow-[0px_2px_4px_rgba(134,134,134,0.16)] lg:hidden"
              }
            />
          </>
        ) : (
          childrenMiddle.map((item, index) => (
            <div key={index} className="mt-2 flex items-start first:mt-0">
              {!item.match(/none/i) && (
                <span>
                  <FaCheckCircle
                    size={16}
                    className="mr-2 mt-1 text-bbc-brand-600"
                  />
                </span>
              )}
              <span
                dangerouslySetInnerHTML={{ __html: item }}
                className="text-sm leading-6 lg:text-base"
              ></span>
            </div>
          ))
        )}
      </div>

      <div
        className={`col-8 p-4 text-lg lg:col-5 lg:p-6 ${
          isHeader ? "text-center font-semibold text-bbc-gray" : "text-left"
        } ${hidden ? "block" : "hidden"} lg:block`}
      >
        {isHeader ? (
          <>
            <HiOutlineChevronRight
              size={32}
              onClick={onClick}
              className={
                "pointer-events float-left inline-block rotate-180 transform cursor-pointer rounded-full bg-bbc-brand-100 p-2 align-middle text-bbc-brand-700 shadow-[0px_-2px_4px_rgba(134,134,134,0.16)] lg:hidden"
              }
            />
            <span className="align-middle">{childrenRight}</span>
          </>
        ) : (
          childrenRight.map((item, index) => (
            <div key={index} className="mt-2 flex items-start first:mt-0">
              {!item.match(/none/i) && (
                <span>
                  <FaCheckCircle
                    size={16}
                    className="mr-2 mt-1 text-bbc-brand-600"
                  />
                </span>
              )}

              <span
                dangerouslySetInnerHTML={{ __html: item }}
                className="text-sm leading-6 lg:text-base"
              ></span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const EconomicSubstance = ({ data, dataJson }) => {
  const {
    country,
    breadcrumbs,
    dataPageType,
    dataTable,
    dataEconomicSubstance,
  } = dataJson;
  const [hidden, setHidden] = useState(false);

  return (
    <>
      <Banner
        breadcrumbs={breadcrumbs}
        title={`${country}<br/> Economic Substance Requirements`}
        titleProps={{
          className: "banner-default-title mb-6 lg:mb-10",
          textColor: "banner-title-default-color",
        }}
        leftClassName="lg:col-7"
        rightClassName="lg:col-5"
        rightContent={
          <Img
            wrapper={{
              className: "bbc-banner-image-country pb-[98px] lg:pb-0",
            }}
            src="/assets/images/common/economic-subtance-banner.png"
            className="w-full "
            width={541}
            height={433}
            priority={true}
            alt={dataJson.imgAlt}
          />
        }
        bgColor={"bg-bbc-country"}
      />

      <div className="absolute left-1/2 -translate-x-1/2 -translate-y-[74px] text-center lg:-translate-y-1/2">
        <PageType data={dataPageType} activeIndex={dataPageType[2].title} />
      </div>
      <section className="mt-10 md:mt-14">
        <div className="container">
          <div className="row justify-between">
            <div className="i flex flex-col justify-center lg:col-6">
              <div className="text-center lg:text-left">
                <Title
                  text={dataEconomicSubstance.title}
                  className="bbc-title mb-6"
                />
                <p
                  className="mb-6"
                  dangerouslySetInnerHTML={{
                    __html: dataEconomicSubstance.description,
                  }}
                ></p>
                <ButtonNew
                  othersClass="mb-6"
                  onClick={async () => {
                    // eslint-disable-next-line no-undef
                    await Tawk_API.toggle();
                  }}
                >
                  Talk To Our Expert Now
                </ButtonNew>
                <ButtonNew
                  as="a"
                  useRouter
                  variant={"ghost"}
                  btnDisplay="block"
                  othersClass="w-fit px-0 py-0 font-medium mx-auto lg:m-0"
                  href="/guide-and-tool/es-tool"
                >
                  Try our ES self-assessment tool
                </ButtonNew>
              </div>
            </div>
            <div className="grid place-items-center lg:col-6">
              <Img
                src={dataEconomicSubstance.src}
                width={540}
                height={540}
                alt="Image Economic Substance"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </section>
      <section>
        {dataJson.dataPricing &&
          typeof dataJson.dataPricing === "object" &&
          Object.keys(dataJson.dataPricing).length > 0 && (
            <EconomicPricing
              data={dataJson.dataPricing}
              className="mb-7 lg:mb-10"
            />
          )}
        {dataTable && Array.isArray(dataTable) && dataTable.length > 0 && (
          <div className="container">
            <Title
              text={`${country} Economic Substance Test`}
              className="bbc-title mb-6 text-center lg:mb-10"
            />

            <div className="shadow-bbc container mb-10 overflow-hidden rounded-xl">
              {dataJson.dataTable.map((item, index) => {
                if (index === 0) {
                  return (
                    <TableRow
                      key={index}
                      childrenLeft={item.left}
                      childrenMiddle={item.middle}
                      childrenRight={item.right}
                      isHeader={true}
                      hidden={hidden}
                      onClick={() => {
                        setHidden(!hidden);
                      }}
                    />
                  );
                }

                return (
                  <TableRow
                    key={index}
                    index={index}
                    childrenLeft={item.left}
                    childrenMiddle={item.middle}
                    childrenRight={item.right}
                    hidden={hidden}
                  />
                );
              })}
            </div>
          </div>
        )}
        {country === "Belize" && (
          <p className="px-4 text-center">
            Need more details on how to file your {country} annual tax return?
            <br />
            Check out this
            <LinkRouter
              href={"/news/belize-annual-tax-return"}
              className="font-medium text-bbc-brand-500"
            >
              {" "}
              quick guide{" "}
            </LinkRouter>
            now &gt;&gt;
          </p>
        )}
      </section>
      <section>
        <div className="container">
          <div className="lg:col-10 lg:m-auto">
            <Faqs data={dataJson.dataFaqs} />
          </div>
        </div>
      </section>
      <section className="mb-0">
        <div className="container">
          <Title className="title-h2 m-auto mb-10 w-2/3 pb-0 text-center text-bbc-brand-800">
            Industry News & Insights
          </Title>
          <LazyLoad>
            <NewsSlider data={data} />
          </LazyLoad>
        </div>
      </section>
    </>
  );
};

export default EconomicSubstance;
